import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { homeSlides } from '../data/projectsData';

const INTERVAL = 5500;

export default function HomeSlideshow() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const region = useRef(null);
  const count = homeSlides.length;

  const go = useCallback((next) => setIndex((i) => (next + count) % count), [count]);

  useEffect(() => {
    if (paused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setTimeout(() => go(index + 1), INTERVAL);
    return () => clearTimeout(t);
  }, [index, paused, go]);

  const onKey = (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
  };

  return (
    <section className="relative bg-[var(--mm-cream)]" aria-roledescription="carousel" aria-label="Mashmari homepage highlights">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div
          ref={region}
          tabIndex={0}
          onKeyDown={onKey}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          data-testid="home-slideshow"
          className="relative rounded-3xl overflow-hidden border border-[var(--mm-line)] bg-[var(--mm-purple-dark)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)] focus-visible:ring-offset-4 focus-visible:ring-offset-[var(--mm-cream)]"
        >
          {homeSlides.map((s, i) => (
            <figure
              key={s.image}
              aria-hidden={i !== index}
              data-testid={`slide-${i}`}
              className={`${i === index ? 'relative opacity-100' : 'absolute inset-0 opacity-0 pointer-events-none'} transition-opacity duration-700`}
            >
              <img src={s.image} alt={s.alt} loading={i === 0 ? 'eager' : 'lazy'}
                className="w-full h-[280px] sm:h-[360px] md:h-[500px] object-contain" />
            </figure>
          ))}

          <button onClick={() => go(index - 1)} aria-label="Previous slide" data-testid="slide-prev"
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/85 hover:bg-white text-[var(--mm-purple)] flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={() => go(index + 1)} aria-label="Next slide" data-testid="slide-next"
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/85 hover:bg-white text-[var(--mm-purple)] flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2" role="tablist" aria-label="Choose slide">
            {homeSlides.map((s, i) => (
              <button key={s.image} onClick={() => setIndex(i)} role="tab" aria-selected={i === index}
                aria-label={`Slide ${i + 1}`} data-testid={`slide-dot-${i}`}
                className={`h-2 rounded-full transition-all ${i === index ? 'w-7 bg-white' : 'w-2 bg-white/45 hover:bg-white/70'}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
